import { ApolloClient } from "apollo-client";
import { InMemoryCache, IntrospectionFragmentMatcher } from "apollo-cache-inmemory";
import { HttpLink } from "apollo-link-http";
import { onError } from "apollo-link-error";
import { ApolloLink } from "apollo-link";
import * as Sentry from "@sentry/browser";

import Context from "shared/util/Context";
import isTest from "shared/util/isTest";
import { isLocalHost } from "shared/util/EnvironmentHelpers";
import LocalEnvironment from "shared/util/LocalEnvironment";
import getDevjailUrl from "shared/util/getDevjailUrl";
import getAccessToken from "shared/util/getAccessToken";
import getPlatform, { PLATFORM } from "shared/util/getPlatform";
import fragmentTypes from "shared/graphql/fragmentTypes.json";

const buildHeaders = () => {
	const res = {};
	const platform = getPlatform();
	const accessToken = getAccessToken();

	if (platform === PLATFORM.Desktop || platform === PLATFORM.Mobile) {
		res["x-okcupid-platform"] = platform;
		res["x-okcupid-version"] = "1";
	} else {
		res["x-okcupid-platform"] = `${platform}_web`;
		res["x-okcupid-version"] = window && window.App ? window.App.app_version : "1";
	}

	if (accessToken) {
		res.Authorization = `Bearer ${accessToken}`;
	}

	return res;
};

export const getGraphqlUrl = () => {
	if (Context.get("env") === "dev" && Context.get("state").GRAPHQL_LOCAL) {
		return `${getDevjailUrl()}/dev-graphql`;
	} else if (Context.get("env") === "dev") {
		return `${getDevjailUrl()}/graphql`;
	} else {
		if (isLocalHost) {
			return `${LocalEnvironment.getEnvironmentUrl()}/graphql`;
		}

		return `https://${window.location.hostname}/graphql`;
	}
};

const fragmentMatcher = new IntrospectionFragmentMatcher({
	introspectionQueryResultData: fragmentTypes,
});

// A cache of the session's ApolloClient.
let cachedClient = null;

const getApolloClient = () => {
	if (isTest()) {
		return {};
	}

	// We've been here before, return the cached client.
	if (cachedClient) {
		return cachedClient;
	}

	// No client cached, create a new client & cache it for later.
	cachedClient = new ApolloClient({
		link: ApolloLink.from([
			onError(({ graphQLErrors, networkError }) => {
				if (graphQLErrors) {
					graphQLErrors.forEach((error) => {
						const { message, locations, path } = error;

						console.error(
							`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
						);

						Sentry.captureException(error);
					});
				}

				if (networkError) {
					console.error(`[Network error]: ${networkError}`);
				}
			}),
			new HttpLink({
				uri: getGraphqlUrl(),
				credentials: "include",
				headers: buildHeaders(),
			}),
		]),
		cache: new InMemoryCache({ fragmentMatcher }),
	});

	return cachedClient;
};

export default getApolloClient;



// WEBPACK FOOTER //
// ./src/shared/util/getApolloClient.js